import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axiosInstance from '../api';
import MermaidDiagram from './MermaidDiagram';
import './OrgDocumentEditor.css';

const WORKFLOW_STEPS = ['DRAFT', 'SUBMITTED', 'ACCEPTED', 'IN_FORCE'];

// Markdown renderers: fenced ```mermaid blocks become diagrams; everything else
// is standard GitHub-flavoured markdown (headings, tables, lists, code).
const MARKDOWN_COMPONENTS = {
  code({ inline, className, children, ...props }) {
    const isMermaid = /language-mermaid/.test(className || '');
    if (!inline && isMermaid) {
      return <MermaidDiagram chart={String(children).replace(/\n$/, '')} />;
    }
    return <code className={className} {...props}>{children}</code>;
  },
};

const WORKFLOW_COLORS = {
  DRAFT:     { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  SUBMITTED: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
  ACCEPTED:  { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
  IN_FORCE:  { bg: '#ede9fe', text: '#5b21b6', border: '#c4b5fd' },
};

function OrgDocumentEditor() {
  const { t } = useTranslation();
  const { id }   = useParams();
  const navigate = useNavigate();

  const WORKFLOW_LABELS = {
    DRAFT: t('OrgDocumentEditor.statusDraft'),
    SUBMITTED: t('OrgDocumentEditor.statusUnderReview'),
    ACCEPTED: t('OrgDocumentEditor.statusAccepted'),
    IN_FORCE: t('OrgDocumentEditor.statusInForce'),
  };

  const [doc, setDoc]               = useState(null);
  const [profile, setProfile]       = useState(null);
  const [content, setContent]       = useState('');
  const [versions, setVersions]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [activePanel, setActivePanel] = useState('edit'); // 'edit' | 'preview' | 'history'
  const [saveComment, setSaveComment] = useState('');
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [dirty, setDirty]           = useState(false);
  const [pdfUrl, setPdfUrl]         = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const role = localStorage.getItem('role');
  // Org admins carry role ROLE_USER + orgRole ORG_ADMIN; the backend (assertOrgManager) allows them.
  const isAdmin = role === 'ROLE_ADMIN' || localStorage.getItem('orgRole') === 'ORG_ADMIN';
  // Only a platform admin lives under /adminHome; org admins (who also hold ROLE_ADMIN) belong under
  // /dashboard. Keying this off isAdmin sent org admins to /adminHome -> route guard -> logout.
  const docsPath = role === 'ROLE_PLATFORM_ADMIN' ? '/adminHome/compliance/documents' : '/dashboard/compliance/documents';

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axiosInstance.get(`/org-documents/${id}`),
      axiosInstance.get('/org-profile'),
    ]).then(async ([docRes, profileRes]) => {
      let d = docRes.data;
      const pdf = (d.contentType || '').toLowerCase().includes('pdf') || (d.fileName || '').toLowerCase().endsWith('.pdf');
      // DOCX file with no editable content yet -> extract to editable Markdown. PDFs are NOT extracted
      // (subsetted-font encoding garbles the text); they are previewed as the original file instead.
      if (!pdf && (!d.content || d.content.trim() === '') && d.fileUrl) {
        try { const ex = await axiosInstance.post(`/org-documents/${id}/extract-content`); d = ex.data; } catch { /* keep going */ }
      }
      setDoc(d);
      setContent(d.content || '');
      setProfile(profileRes.data);
      if (pdf && d.fileUrl) {
        try {
          const fileRes = await axiosInstance.get(`/org-documents/${id}/file`, { responseType: 'blob' });
          setPdfUrl(window.URL.createObjectURL(new Blob([fileRes.data], { type: 'application/pdf' })));
        } catch { /* preview unavailable */ }
      }
    }).catch(() => {
      alert(t('OrgDocumentEditor.loadFailed'));
      navigate(docsPath);
    }).finally(() => setLoading(false));
  }, [id, navigate, docsPath, t]);

  const loadVersions = useCallback(() => {
    axiosInstance.get(`/org-documents/${id}/versions`)
      .then(res => setVersions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setVersions([]));
  }, [id]);

  useEffect(() => {
    load();
    loadVersions();
  }, [load, loadVersions]);

  const handleDownloadOriginal = async () => {
    try {
      const res = await axiosInstance.get(`/org-documents/${id}/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc?.fileName || doc?.title || 'document');
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert(t('OrgDocumentEditor.loadFailed')); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance.put(`/org-documents/${id}/content`, {
        content,
        comment: saveComment || t('OrgDocumentEditor.manualSave'),
      });
      setDoc(res.data);
      setDirty(false);
      setShowCommentBox(false);
      setSaveComment('');
      loadVersions();
    } catch {
      alert(t('OrgDocumentEditor.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      let res;
      if (action === 'submit')   res = await axiosInstance.post(`/org-documents/${id}/submit`);
      if (action === 'approve')  res = await axiosInstance.post(`/org-documents/${id}/approve`);
      if (action === 'reject')   res = await axiosInstance.post(`/org-documents/${id}/reject`);
      if (action === 'in-force') res = await axiosInstance.post(`/org-documents/${id}/in-force`);
      if (res) { setDoc(res.data); loadVersions(); }
    } catch (err) {
      alert(err?.response?.data?.message || t('OrgDocumentEditor.actionFailed'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestoreVersion = (version) => {
    if (!window.confirm(t('OrgDocumentEditor.restoreConfirm', { version: version.versionNumber }))) return;
    setContent(version.content || '');
    setSaveComment(t('OrgDocumentEditor.restoredFromVersion', { version: version.versionNumber }));
    setDirty(true);
    setActivePanel('edit');
  };

  if (loading) {
    return (
      <div className="ode-loading">
        <div className="ode-spinner" />
        <p>{t('OrgDocumentEditor.loadingDocument')}</p>
      </div>
    );
  }
  if (!doc) return null;

  const status = doc.workflowStatus || 'DRAFT';
  const wfColors = WORKFLOW_COLORS[status] || WORKFLOW_COLORS.DRAFT;
  const stepIndex = WORKFLOW_STEPS.indexOf(status);
  const canEdit = status === 'DRAFT';
  const isPdf = doc && ((doc.contentType || '').toLowerCase().includes('pdf') || (doc.fileName || '').toLowerCase().endsWith('.pdf'));
  const canSubmit = status === 'DRAFT' && !dirty;
  const canApprove = isAdmin && status === 'SUBMITTED';
  const canReject = isAdmin && status === 'SUBMITTED';
  const canSetInForce = isAdmin && status === 'ACCEPTED';

  const orgName  = profile?.organizationName || t('OrgDocumentEditor.yourOrganization');
  const orgUrl   = profile?.orgWebsiteUrl || '';
  const orgLogo  = profile?.logoUrl || null;
  const ownerName = doc.ownedBy
    ? `${doc.ownedBy.firstName || ''} ${doc.ownedBy.lastName || ''}`.trim()
    : '';
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="ode-layout">
      {/* ── Top bar ─────────────────────────────────── */}
      <div className="ode-topbar">
        <button className="ode-back-btn" onClick={() => navigate(docsPath)}>
          <i className="fas fa-arrow-left" /> {t('OrgDocumentEditor.documents')}
        </button>
        <div className="ode-topbar-center">
          <span className="ode-doc-title">{doc.title}</span>
          <span
            className="ode-status-badge"
            style={{ background: wfColors.bg, color: wfColors.text, border: `1px solid ${wfColors.border}` }}
          >
            {WORKFLOW_LABELS[status] || status}
          </span>
          <span className="ode-version-tag">v{doc.currentVersion}</span>
        </div>
        <div className="ode-topbar-right">
          {doc.fileUrl && (
            <button className="ode-btn" onClick={handleDownloadOriginal} title="Download the original uploaded file">
              <i className="fas fa-download" /> {t('OrgDocumentEditor.downloadOriginal', 'Download original')}
            </button>
          )}
          {dirty && canEdit && (
            <button
              className="ode-btn ode-btn-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
              {saving ? ` ${t('OrgDocumentEditor.saving')}` : ` ${t('OrgDocumentEditor.save')}`}
            </button>
          )}
          {canSubmit && (
            <button
              className="ode-btn ode-btn-submit"
              onClick={() => handleAction('submit')}
              disabled={!!actionLoading}
            >
              {actionLoading === 'submit' ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-paper-plane" />}
              {` ${t('OrgDocumentEditor.submitForReview')}`}
            </button>
          )}
          {canApprove && (
            <button
              className="ode-btn ode-btn-approve"
              onClick={() => handleAction('approve')}
              disabled={!!actionLoading}
            >
              {actionLoading === 'approve' ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
              {` ${t('OrgDocumentEditor.approve')}`}
            </button>
          )}
          {canReject && (
            <button
              className="ode-btn ode-btn-reject"
              onClick={() => handleAction('reject')}
              disabled={!!actionLoading}
            >
              {actionLoading === 'reject' ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-times" />}
              {` ${t('OrgDocumentEditor.reject')}`}
            </button>
          )}
          {canSetInForce && (
            <button
              className="ode-btn ode-btn-inforce"
              onClick={() => handleAction('in-force')}
              disabled={!!actionLoading}
            >
              {actionLoading === 'in-force' ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-gavel" />}
              {` ${t('OrgDocumentEditor.setInForce')}`}
            </button>
          )}
        </div>
      </div>

      {/* ── Workflow stepper ─────────────────────────── */}
      <div className="ode-stepper">
        {WORKFLOW_STEPS.map((step, i) => (
          <React.Fragment key={step}>
            <div className={`ode-step ${i <= stepIndex ? 'ode-step-done' : ''} ${i === stepIndex ? 'ode-step-active' : ''}`}>
              <div className="ode-step-dot">
                {i < stepIndex
                  ? <i className="fas fa-check" />
                  : i + 1}
              </div>
              <span className="ode-step-label">{WORKFLOW_LABELS[step]}</span>
            </div>
            {i < WORKFLOW_STEPS.length - 1 && (
              <div className={`ode-step-line ${i < stepIndex ? 'ode-step-line-done' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ── Panel tabs ──────────────────────────────── */}
      <div className="ode-panels">
        <div className="ode-panel-tabs">
          {['edit', 'preview', 'history'].map(p => (
            <button
              key={p}
              className={`ode-panel-tab ${activePanel === p ? 'ode-panel-tab-active' : ''}`}
              onClick={() => setActivePanel(p)}
            >
              {p === 'edit'    && <><i className="fas fa-edit" /> {t('OrgDocumentEditor.tabEdit')}</>}
              {p === 'preview' && <><i className="fas fa-eye" /> {t('OrgDocumentEditor.tabPreview')}</>}
              {p === 'history' && <><i className="fas fa-history" /> {t('OrgDocumentEditor.tabVersionHistory')} {versions.length > 0 && <span className="ode-ver-count">{versions.length}</span>}</>}
            </button>
          ))}
        </div>

        {/* Edit panel */}
        {activePanel === 'edit' && (
          <div className="ode-edit-panel">
            {!canEdit && (
              <div className="ode-readonly-notice">
                <i className="fas fa-lock" />
                {` ${t('OrgDocumentEditor.documentIs')} `}
                <strong>{WORKFLOW_LABELS[status]}</strong>
                {` ${t('OrgDocumentEditor.editingDisabled')} `}
                {isAdmin && status === 'ACCEPTED' && t('OrgDocumentEditor.adminUnlockHint')}
                {!isAdmin && t('OrgDocumentEditor.contactAdminHint')}
              </div>
            )}
            {isPdf ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '10px 14px', color: '#9a3412', fontSize: 13 }}>
                  <i className="fas fa-file-pdf" style={{ marginRight: 8 }} />
                  This is a PDF, shown as your original file below. In-app text editing isn't available for PDFs — to edit inline, upload the Word (.docx) version, or use "Download original".
                </div>
                {pdfUrl
                  ? <iframe title="PDF preview" src={pdfUrl} style={{ width: '100%', height: '70vh', border: '1px solid #e2e8f0', borderRadius: 8 }} />
                  : <div style={{ padding: 20, color: '#94a3b8' }}>Loading preview…</div>}
              </div>
            ) : (
              <textarea
                className="ode-editor"
                value={content}
                readOnly={!canEdit}
                onChange={e => { setContent(e.target.value); setDirty(true); }}
                placeholder={t('OrgDocumentEditor.editorPlaceholder')}
                spellCheck
              />
            )}
            {showCommentBox && (
              <div className="ode-comment-bar">
                <input
                  className="ode-comment-input"
                  placeholder={t('OrgDocumentEditor.versionCommentPlaceholder')}
                  value={saveComment}
                  onChange={e => setSaveComment(e.target.value)}
                />
                <button className="ode-btn ode-btn-save" onClick={handleSave} disabled={saving}>
                  {saving ? t('OrgDocumentEditor.saving') : t('OrgDocumentEditor.confirmSave')}
                </button>
                <button className="ode-btn ode-btn-cancel" onClick={() => setShowCommentBox(false)}>{t('OrgDocumentEditor.cancel')}</button>
              </div>
            )}
          </div>
        )}

        {/* Preview panel — branded document */}
        {activePanel === 'preview' && (
          <div className="ode-preview-wrap">
            <div className="ode-document-page">
              {/* Header */}
              <div className="ode-doc-header">
                <div className="ode-doc-header-left">
                  {orgLogo
                    ? <img src={orgLogo} alt={orgName} className="ode-org-logo" />
                    : <div className="ode-org-logo-placeholder"><i className="fas fa-building" /></div>}
                  <div>
                    <div className="ode-org-name">{orgName}</div>
                    {orgUrl && <div className="ode-org-url">{orgUrl}</div>}
                  </div>
                </div>
                <div className="ode-doc-header-right">
                  <div className="ode-doc-meta-label">{t('OrgDocumentEditor.documentLabel')}</div>
                  <div className="ode-doc-meta-id">{doc.sourceTemplate?.documentId || 'ORG-DOC'}</div>
                  <div className="ode-doc-meta-ver">{t('OrgDocumentEditor.versionLabel', { version: doc.currentVersion })}</div>
                  <div className="ode-doc-meta-status" style={{ color: wfColors.text }}>
                    {WORKFLOW_LABELS[status]}
                  </div>
                </div>
              </div>

              {/* Title */}
              <div className="ode-doc-title-block">
                <h1 className="ode-doc-h1">{doc.title}</h1>
              </div>

              {/* Body */}
              <div className="ode-doc-body ode-doc-markdown">
                {isPdf ? (
                  pdfUrl
                    ? <iframe title="PDF preview" src={pdfUrl} style={{ width: '100%', height: '70vh', border: 'none' }} />
                    : <div style={{ padding: 20, color: '#94a3b8' }}>Loading preview…</div>
                ) : (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
                    {content || ''}
                  </ReactMarkdown>
                )}
              </div>

              {/* Footer */}
              <div className="ode-doc-footer">
                <div className="ode-doc-footer-left">
                  <span>{orgName}</span>
                  {orgUrl && <span> · {orgUrl}</span>}
                </div>
                <div className="ode-doc-footer-center">
                  {ownerName && <span>{t('OrgDocumentEditor.documentOwner', { name: ownerName })}</span>}
                </div>
                <div className="ode-doc-footer-right">
                  <span>{today}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Version History panel */}
        {activePanel === 'history' && (
          <div className="ode-history-panel">
            {versions.length === 0 ? (
              <div className="ode-history-empty">{t('OrgDocumentEditor.noVersionHistory')}</div>
            ) : (
              <div className="ode-history-list">
                {versions.map((v, idx) => (
                  <div key={v.id} className={`ode-history-item ${idx === 0 ? 'ode-history-current' : ''}`}>
                    <div className="ode-history-meta">
                      <span className="ode-ver-badge">v{v.versionNumber}</span>
                      <span className="ode-history-date">
                        {v.changedAt ? new Date(v.changedAt).toLocaleString() : '—'}
                      </span>
                      <span className="ode-history-author">
                        {v.changedBy ? `${v.changedBy.firstName} ${v.changedBy.lastName}` : t('OrgDocumentEditor.unknown')}
                      </span>
                      {idx === 0 && <span className="ode-current-tag">{t('OrgDocumentEditor.current')}</span>}
                    </div>
                    {v.comment && <div className="ode-history-comment">{v.comment}</div>}
                    <div className="ode-history-preview">{(v.content || '').slice(0, 160)}{(v.content || '').length > 160 ? '…' : ''}</div>
                    {idx > 0 && canEdit && (
                      <button
                        className="ode-restore-btn"
                        onClick={() => handleRestoreVersion(v)}
                      >
                        <i className="fas fa-undo" /> {t('OrgDocumentEditor.restoreThisVersion')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrgDocumentEditor;
